import React, { useState, useEffect, Fragment } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { Check, ChevronDown, X } from 'lucide-react';
import { Parameter, DataType } from '../../types/schema';
import { getTypeColorClass } from '../../utils/schemaUtils';
import clsx from 'clsx';
import {
  TypeIconString,
  TypeIconNumber,
  TypeIconBoolean,
  TypeIconObject,
  TypeIconArray,
  TypeIconNull,
} from '@components/common/DataTypeIcons';

interface PropertyPathAutocompleteProps {
  label: string;
  value: string;
  onSelect: (fullPath: string) => void;
  availableProperties: Parameter[];
  usedProperties?: string[];
  expectedDataType?: DataType | 'any';
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  unstyled?: boolean;
}

const DataTypeIcon: React.FC<{ type: DataType; className?: string }> = ({ type, className = "h-4 w-4" }) => {
  switch (type) {
    case 'string': return <TypeIconString className={className} />;
    case 'number': return <TypeIconNumber className={className} />;
    case 'boolean': return <TypeIconBoolean className={className} />;
    case 'object': return <TypeIconObject className={className} />;
    case 'array': return <TypeIconArray className={className} />;
    case 'null': return <TypeIconNull className={className} />;
    default: return null;
  }
};

const PropertyPathAutocomplete = React.forwardRef<HTMLInputElement, PropertyPathAutocompleteProps>(({
  label,
  value,
  onSelect,
  availableProperties,
  usedProperties = [],
  expectedDataType = 'any',
  placeholder,
  disabled,
  className,
  unstyled = false,
}, ref) => {
  const [query, setQuery] = useState('');
  const [selectedProperty, setSelectedProperty] = useState<Parameter | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [inputElement, setInputElement] = useState<HTMLInputElement | null>(null);
  
  // Merge external ref with internal ref
  const mergedRef = React.useCallback((node: HTMLInputElement | null) => {
    // Set internal state
    setInputElement(node);
    
    // Set external ref
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
    }
  }, [ref]);

  // Find the selected property based on the value
  useEffect(() => {
    if (value) {
      const property = availableProperties.find(p => p.fullPath === value);
      setSelectedProperty(property || null);
    } else {
      setSelectedProperty(null);
    }
  }, [value, availableProperties]);
  
  // Reset query when value changes externally
  useEffect(() => {
    if (value !== (selectedProperty?.fullPath || '') && selectedProperty !== null) {
      setQuery('');
    }
  }, [value, selectedProperty]);

  // Filter properties based on query, used properties, and expected data type
  const filteredProperties = availableProperties.filter((property) => {
    // Filter by query if there is one
    const matchesQuery = query === '' || 
                        (property.fullPath || '').toLowerCase().includes(query.toLowerCase()) ||
                        (property.friendlyName || '').toLowerCase().includes(query.toLowerCase()) ||
                        (property.codeName || '').toLowerCase().includes(query.toLowerCase());
    
    // Filter out already used properties
    const isUsed = usedProperties.includes(property.fullPath || '');

    // Filter by expected data type if specified
    const matchesType = expectedDataType === 'any' || 
                        (property.dataTypes && property.dataTypes.includes(expectedDataType));

    return matchesQuery && !isUsed && matchesType;
  });

  const handleSelect = (property: Parameter | null) => {
    setSelectedProperty(property);
    onSelect(property?.fullPath || '');
    setQuery('');
    setIsOpen(false); // Close dropdown when an option is selected
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedProperty(null);
    onSelect('');
    setQuery('');
    // Focus the input and keep the dropdown open after clearing
    setTimeout(() => {
      inputElement?.focus();
      setIsOpen(true);
    }, 10);
  };

  // Prevent dropdown from closing when clicking the chevron button
  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <div className={clsx(!unstyled && "relative mt-1", className)}>
      {label && (
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}
      <Combobox value={selectedProperty} onChange={handleSelect} disabled={disabled}>
        <div className="relative w-full">
          <div className={clsx("relative w-full cursor-default overflow-hidden text-left", !unstyled && "border border-gray-300 dark:border-gray-600 rounded")}>
            <div className="flex items-center">
              {selectedProperty && !unstyled && (
                <div className={clsx(
                  'absolute left-3 flex items-center justify-center h-5 w-5 rounded-full',
                  getTypeColorClass(selectedProperty.dataTypes[0] || 'string')
                )}>
                  <DataTypeIcon type={selectedProperty.dataTypes[0] || 'string'} className="h-3 w-3" />
                </div>
              )}
              <Combobox.Input
                ref={mergedRef}
                className={clsx(
                  "w-full text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out disabled:opacity-50",
                  !unstyled && "px-3 py-2 border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-800 dark:focus:bg-gray-700",
                  selectedProperty && !unstyled ? "pl-10" : !unstyled && "pl-3",
                  !unstyled && "pr-10",
                  unstyled && "bg-transparent focus:outline-none"
                )}
                displayValue={(property: Parameter | null) => property?.fullPath || ''}
                onChange={(event) => setQuery(event.target.value)}
                onFocus={() => setIsOpen(true)}
                onBlur={(e) => {
                  // Only close if the related target is not the dropdown button or options
                  if (!e.relatedTarget || 
                      !e.relatedTarget.closest('.dropdown-button') && 
                      !e.relatedTarget.closest('.dropdown-options')) {
                    setTimeout(() => {
                      setIsOpen(false);
                    }, 150);
                  }
                }}
                placeholder={placeholder}
                autoComplete="off"
              />
            </div>
            
            {selectedProperty && ( // Show clear button only if a value is selected
              <button
                type="button"
                onClick={handleClear}
                className="absolute inset-y-0 right-8 flex items-center pr-2 text-gray-400 hover:text-gray-600"
                aria-label="Clear selection"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            
            <Combobox.Button 
              className="absolute inset-y-0 right-0 flex items-center pr-2 dropdown-button"
              onClick={handleButtonClick}
            >
              <ChevronDown
                className={clsx(
                  "h-5 w-5 text-gray-400 transition-transform duration-200",
                  isOpen && "transform rotate-180"
                )}
                aria-hidden="true"
              />
            </Combobox.Button>
          </div>
          
          <Transition
            as={Fragment}
            show={isOpen}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm dark:bg-gray-800 dark:border dark:border-gray-700 dropdown-options">
              {filteredProperties.length === 0 ? (
                <div className="relative cursor-default select-none py-2 px-4 text-gray-700 dark:text-gray-300">
                  {query !== '' ? 'Nothing found.' : 'No available properties.'}
                </div>
              ) : (
                filteredProperties.map((property) => (
                  <Combobox.Option
                    key={property.id}
                    className={({ active }) =>
                      clsx(
                        'relative cursor-pointer select-none py-2 pl-10 pr-4 hover:bg-blue-600 hover:text-white',
                        active ? 'bg-blue-600 text-white' : 'text-gray-900 dark:text-gray-100'
                      )
                    }
                    value={property}
                  >
                    {({ selected, active }) => (
                      <div className="flex items-center">
                        <span className={clsx(
                          'absolute inset-y-0 left-0 flex items-center pl-3',
                          active ? 'text-white' : 'text-blue-600'
                        )}>
                          {selected && <Check className="h-5 w-5" aria-hidden="true" />}
                        </span>
                        
                        {/* Data Type Icon */}
                        <span className={clsx(
                          'flex items-center justify-center h-5 w-5 rounded-full mr-2',
                          getTypeColorClass(property.dataTypes[0] || 'string')
                        )}>
                          <DataTypeIcon type={property.dataTypes[0] || 'string'} className="h-3 w-3" />
                        </span>
                        
                        <div className="flex flex-col">
                          <span className={clsx('block truncate', selected ? 'font-medium' : 'font-normal')}>
                            {property.fullPath}
                          </span>
                          {property.friendlyName && property.friendlyName !== property.fullPath && (
                            <span className={clsx(
                              'text-xs truncate',
                              active ? 'text-blue-200' : 'text-gray-500 dark:text-gray-400'
                            )}>
                              {property.friendlyName}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
});

PropertyPathAutocomplete.displayName = 'PropertyPathAutocomplete';

export default PropertyPathAutocomplete;
